import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiAlertCircle, FiGrid, FiKey, FiList, FiMenu, FiLogOut, FiPlus, FiRefreshCcw, FiSave, FiUser } from "react-icons/fi";
import favicon from "../assets/favicon.png";
import { BASE_URL, hospitalPost } from "../utils/hospitalApi";

const directWalkin = "Direct Hospital Walking";
const initialValues = {
  customer_name: "",
  phone_number: "",
  gender: "",
  age: "",
  city_area: "",
  disposition: "",
  disposition_other: "",
  appointment_date: "",
  appointment_time: "",
  consultation_fee: "",
  advance_fee: "",
  advance_transaction_id: "",
  appointment_done: "NO",
};

const dispositions = ["Follow Up", "Center", "Walk-in", "Referral", "Other"];

const HospitalPatientEntry = () => {
  const navigate = useNavigate();
  const hospitalUserId = localStorage.getItem("hospital_user_id");
  const hospital = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("hospital_user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const profileImage = `${BASE_URL}assets/hospital/${hospital.foto || "user.png"}`;
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const balanceFee = useMemo(() => {
    const fee = Number(values.consultation_fee || 0);
    const advance = Number(values.advance_fee || 0);
    return Math.max(fee - advance, 0).toFixed(2);
  }, [values.consultation_fee, values.advance_fee]);

  React.useEffect(() => {
    if (!hospitalUserId) navigate("/");
  }, [hospitalUserId, navigate]);

  const logout = () => {
    localStorage.removeItem("hospital_user_id");
    localStorage.removeItem("hospital_user");
    navigate("/");
  };

  const validateForm = (nextValues = values) => {
    const nextErrors = {};
    if (!nextValues.customer_name.trim()) nextErrors.customer_name = "Required";
    if (!/^[1-9][0-9]{9}$/.test(nextValues.phone_number)) nextErrors.phone_number = "Enter valid mobile";
    if (!nextValues.gender) nextErrors.gender = "Select Gender";
    if (!nextValues.age || Number(nextValues.age) < 1 || Number(nextValues.age) > 120) nextErrors.age = "Enter valid age";
    if (!nextValues.city_area.trim()) nextErrors.city_area = "Required";
    if (!nextValues.disposition) nextErrors.disposition = "Select Disposition";
    if (nextValues.disposition === "Other" && !nextValues.disposition_other.trim()) nextErrors.disposition_other = "Enter disposition";
    if (!nextValues.appointment_date) nextErrors.appointment_date = "Enter Appointment Date";
    if (!nextValues.appointment_time) nextErrors.appointment_time = "Enter Appointment Time";
    if (Number(nextValues.advance_fee || 0) > 0 && !nextValues.advance_transaction_id.trim()) nextErrors.advance_transaction_id = "Transaction ID required";
    return nextErrors;
  };

  const changeValue = (event) => {
    const { name, value } = event.target;
    const nextValues = {
      ...values,
      [name]: name === "phone_number" ? value.replace(/\D/g, "").slice(0, 10) : value,
      ...(name === "disposition" && value !== "Other" ? { disposition_other: "" } : {}),
    };
    setValues(nextValues);
    if (submitted) setErrors(validateForm(nextValues));
  };

  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setSubmitted(false);
  };

  const submitEntry = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    const nextErrors = validateForm();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    const res = await hospitalPost("hospital-create-appointment", {
      ...values,
      hospital_user_id: hospitalUserId,
      pending_fee: balanceFee,
      source_name: directWalkin,
      agent_name: directWalkin,
      agent_emp_code: directWalkin,
    });
    setSaving(false);
    if (res.data.status === 1) {
      toast.success(`${res.data.msg || "Appointment booked"}! Patient ID: ${res.data.patient_id}`);
      resetForm();
      navigate("/appointments");
    } else {
      toast.error(res.data.msg || "Unable to book appointment");
    }
  };

  const fieldClass = (name) => `${errors[name] ? " is-invalid" : ""}`;
  const errorText = (name) => errors[name] ? <div className="h-error"><FiAlertCircle /> {errors[name]}</div> : null;

  return (
    <div className="hospital-shell">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
        .hospital-shell { min-height:100vh; background:#edf3f7; font-family:'Nunito','DM Sans',sans-serif; color:#102033; display:flex; }
        .h-sidebar { width:118px; background:#07182e; color:#fff; min-height:100vh; position:sticky; top:0; align-self:flex-start; box-shadow:10px 0 28px rgba(15,23,42,.10); z-index:5; }
        .h-brand { height:68px; display:flex; align-items:center; justify-content:center; border-bottom:1px solid rgba(255,255,255,.09); }
        .h-brand img { width:34px; height:34px; object-fit:contain; }
        .h-nav { display:flex; flex-direction:column; padding:0; }
        .h-nav button { min-height:84px; border:0; background:transparent; color:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:10px; font-size:13px; font-weight:900; border-bottom:1px solid rgba(255,255,255,.10); cursor:pointer; }
        .h-nav button.is-active, .h-nav button:hover { background:#fff; color:#0f172a; }
        .h-nav button svg { font-size:24px; color:#9ee7dc; }
        .h-nav button.is-active svg, .h-nav button:hover svg { color:#0f172a; }
        .h-sidebar-logout { position:absolute; left:10px; right:10px; bottom:12px; }
        .h-main { flex:1; min-width:0; }
        .h-topbar { height:66px; background:#07182e; color:#fff; display:flex; align-items:center; justify-content:space-between; padding:0 20px; box-shadow:0 6px 18px rgba(15,23,42,.12); }
        .h-top-title { display:flex; align-items:center; gap:8px; font-size:20px; font-weight:900; letter-spacing:.4px; }
        .h-profile-wrap { position:relative; }
        .h-profile { border:0; background:transparent; color:#fff; display:flex; align-items:center; gap:10px; cursor:pointer; padding:3px 0; }
        .h-profile-name { font-size:14px; font-weight:900; text-align:right; }
        .h-profile-sub { font-size:11px; color:#cbd5e1; text-align:right; }
        .h-avatar { width:42px; height:42px; border-radius:50%; background:linear-gradient(135deg,#14b8a6,#0f766e); display:flex; align-items:center; justify-content:center; border:3px solid rgba(255,255,255,.16); overflow:hidden; flex:0 0 auto; }
        .h-avatar img { width:100%; height:100%; object-fit:cover; }
        .h-profile-menu { position:absolute; right:0; top:54px; min-width:190px; background:#fff; color:#0f172a; border:1px solid #dbe3ef; border-radius:13px; box-shadow:0 18px 44px rgba(15,23,42,.18); padding:7px; z-index:20; }
        .h-profile-menu button { width:100%; border:0; background:transparent; color:#0f172a; display:flex; align-items:center; gap:9px; padding:10px 11px; border-radius:10px; font-size:12px; font-weight:900; text-align:left; cursor:pointer; }
        .h-profile-menu button:hover { background:#eff6ff; color:#0f766e; }
        .h-content { padding:14px 14px 64px; }
        .h-footer { position:fixed; left:118px; right:0; bottom:0; height:46px; background:#fff; border-top:1px solid #e5e7eb; display:flex; align-items:center; justify-content:center; z-index:4; color:#8a94a6; font-size:12px; font-weight:700; box-shadow:0 -6px 18px rgba(15,23,42,.06); }
        .h-footer strong { color:#f05a00; font-weight:900; }
        .h-page-head { min-height:118px; border-radius:22px; background:linear-gradient(120deg,#133761 0%,#0f766e 72%,#8b6f34 100%); color:#fff; padding:24px; display:flex; align-items:center; justify-content:space-between; gap:16px; margin-bottom:16px; box-shadow:0 18px 45px rgba(15,23,42,.16); }
        .h-kicker { display:inline-flex; padding:7px 12px; border-radius:999px; background:rgba(255,255,255,.15); color:#dcfce7; font-size:11px; font-weight:900; letter-spacing:.8px; text-transform:uppercase; margin-bottom:10px; }
        .h-page-head h1 { margin:0; font-size:28px; line-height:1.1; font-weight:900; }
        .h-page-head p { margin:8px 0 0; color:#dbeafe; font-weight:800; }
        .h-card { background:#fff; border:1px solid #e2e8f0; border-radius:16px; box-shadow:0 8px 26px rgba(15,23,42,.06); margin-bottom:16px; overflow:hidden; }
        .h-card-head { background:#07182e; color:#fff; padding:14px 18px; display:flex; align-items:center; gap:9px; font-weight:900; }
        .h-form-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(230px,1fr)); gap:14px 16px; padding:18px; }
        .h-label { display:block; font-size:11px; font-weight:900; color:#64748b; text-transform:uppercase; margin-bottom:5px; }
        .h-input, .h-select { width:100%; height:38px; border:1px solid #dbe3ef; border-radius:9px; font-size:12px; padding:0 10px; outline:none; background:#f8fafc; color:#0f172a; font-weight:700; }
        .h-input:focus, .h-select:focus { border-color:#14b8a6; box-shadow:0 0 0 3px rgba(20,184,166,.11); }
        .h-input[readonly] { color:#475569; background:#eef6f7; }
        .h-input.is-invalid, .h-select.is-invalid { border-color:#ff4268; background:#fff; }
        .h-error { display:flex; align-items:center; gap:4px; margin-top:6px; color:#ff3158; font-size:11px; font-weight:800; }
        .h-error svg { flex:0 0 auto; }
        .h-actions { display:flex; gap:9px; flex-wrap:wrap; padding:0 18px 18px; }
        .h-btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; border:none; border-radius:9px; padding:9px 13px; font-size:12px; font-weight:900; cursor:pointer; white-space:nowrap; }
        .h-btn-primary { background:#0f766e; color:#fff; }
        .h-btn-soft { background:#e6fffb; color:#0f766e; }
        .h-btn-danger { background:#fee2e2; color:#dc2626; }
        .h-note { margin:0 18px 16px; border:1px solid #dbeafe; background:#eff6ff; color:#1e40af; border-radius:12px; padding:12px 14px; font-size:12px; font-weight:800; }
        @media (max-width:980px) { .hospital-shell { display:block; } .h-footer { left:0; } .h-sidebar { position:relative; width:100%; min-height:auto; display:flex; align-items:center; overflow-x:auto; } .h-brand { flex:0 0 96px; width:96px; height:66px; border-bottom:0; } .h-nav { flex:1; flex-direction:row; overflow-x:auto; } .h-nav button { min-height:66px; min-width:104px; border-bottom:0; border-left:1px solid rgba(255,255,255,.08); } .h-sidebar-logout { position:static; flex:0 0 auto; padding:10px; min-width:112px; } .h-sidebar-logout .h-btn { width:100%; } .h-topbar { height:auto; padding:12px; } .h-page-head { flex-direction:column; align-items:flex-start; } }
        @media (max-width:560px) { .h-content { padding:10px 10px 64px; } .h-page-head { padding:20px; border-radius:18px; } .h-page-head h1 { font-size:24px; } .h-profile-name, .h-profile-sub { display:none; } .h-form-grid { grid-template-columns:1fr; padding:14px; } .h-actions { padding:0 14px 14px; } .h-btn { width:100%; } }
        .h-menu-toggle { display:none; width:38px; height:38px; border:0; border-radius:10px; background:#e6fffb; color:#0f766e; align-items:center; justify-content:center; font-size:20px; cursor:pointer; }
        .h-sidebar-backdrop { display:none; }
        @media (max-width:980px) {
          .h-menu-toggle { display:inline-flex; flex:0 0 auto; }
          .h-topbar { gap:10px; }
          .h-top-title { flex:1; min-width:0; font-size:17px; }
          .hospital-shell { display:block; }
          .h-main { min-width:0; }
          .h-footer { left:0 !important; }
          .h-sidebar { position:fixed !important; left:0; top:0; bottom:0; width:244px !important; min-height:100vh !important; transform:translateX(-105%); transition:transform .22s ease; display:block !important; overflow:auto !important; z-index:60; box-shadow:18px 0 42px rgba(7,24,46,.28); }
          .h-sidebar.is-open { transform:translateX(0); }
          .h-sidebar-backdrop { display:block; position:fixed; inset:0; border:0; background:rgba(7,24,46,.55); z-index:55; }
          .h-brand { width:auto !important; height:76px !important; flex:0 0 auto !important; border-bottom:1px solid rgba(255,255,255,.09) !important; margin:0 !important; }
          .h-nav { flex:0 0 auto !important; flex-direction:column !important; overflow:visible !important; }
          .h-nav button { min-height:62px !important; min-width:0 !important; width:100% !important; flex-direction:row !important; justify-content:flex-start !important; gap:12px !important; padding:0 18px !important; border-left:0 !important; border-bottom:1px solid rgba(255,255,255,.10) !important; }
          .h-nav button svg { font-size:20px !important; }
          .h-sidebar-logout { position:static !important; padding:14px !important; min-width:0 !important; }
          .h-sidebar-logout .h-btn { width:100% !important; min-height:42px; }
        }
      `}</style>

      <aside className={`h-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="h-brand"><img src={favicon} alt="Sat Kartar" /></div>
        <nav className="h-nav">
          <button type="button" onClick={() => navigate("/dashboard")}><FiGrid /> Dashboard</button>
          <button className="is-active" type="button"><FiPlus /> Patient Entry</button>
          <button type="button" onClick={() => navigate("/appointments")}><FiList /> Appointment List</button>
          <button type="button" onClick={() => navigate("/profile")}><FiUser /> My Profile</button>
        </nav>
        <div className="h-sidebar-logout"><button className="h-btn h-btn-danger" onClick={logout}><FiLogOut size={14} /> Logout</button></div>
      </aside>
      {sidebarOpen ? <button className="h-sidebar-backdrop" type="button" aria-label="Close menu" onClick={() => setSidebarOpen(false)} /> : null}

      <main className="h-main">
        <header className="h-topbar">
          <button className="h-menu-toggle" type="button" aria-label="Open menu" onClick={() => setSidebarOpen(true)}><FiMenu /></button>
          <div className="h-top-title"><span style={{ color: "#ff4b21" }}>SAT KARTAR</span><span style={{ color: "#61a5ff" }}>SANJEEVAN</span></div>
          <div className="h-profile-wrap" onMouseLeave={() => setProfileOpen(false)}>
            <button className="h-profile" type="button" onMouseEnter={() => setProfileOpen(true)} onClick={() => setProfileOpen(true)}>
              <div><div className="h-profile-name">{hospital.hospital_name || "Hospital User"}</div><div className="h-profile-sub">Hospital Panel</div></div>
              <div className="h-avatar"><img src={profileImage} alt="Profile" onError={(e) => { e.currentTarget.src = `${BASE_URL}assets/hospital/user.png`; }} /></div>
              
            </button>
            {profileOpen ? <div className="h-profile-menu"><button type="button" onClick={() => navigate("/change-password")}><FiKey /> Change Password</button><button type="button" onClick={() => navigate("/profile")}><FiUser /> My Profile</button></div> : null}
          </div>
        </header>

        <div className="h-content">
          <section className="h-page-head">
            <div><div className="h-kicker">Walk-in Entry</div><h1>Hospital Patient Entry</h1><p>Book appointments for patients who directly visit the hospital.</p></div>
            <button className="h-btn h-btn-soft" type="button" onClick={() => navigate("/appointments")}><FiList size={13} /> Patient List</button>
          </section>

          <form className="h-card" onSubmit={submitEntry} noValidate>
            <div className="h-card-head"><FiPlus /> New Hospital Entry</div>
            <div className="h-form-grid">
              <div><label className="h-label">Customer Name</label><input className={`h-input${fieldClass("customer_name")}`} name="customer_name" value={values.customer_name} onChange={changeValue} placeholder="Customer name" />{errorText("customer_name")}</div>
              <div><label className="h-label">Phone Number</label><input className={`h-input${fieldClass("phone_number")}`} name="phone_number" value={values.phone_number} onChange={changeValue} placeholder="10 digit mobile" maxLength="10" />{errorText("phone_number")}</div>
              <div><label className="h-label">Gender</label><select className={`h-select${fieldClass("gender")}`} name="gender" value={values.gender} onChange={changeValue}><option value="">Select Gender</option><option>Male</option><option>Female</option><option>Other</option></select>{errorText("gender")}</div>
              <div><label className="h-label">Age</label><input className={`h-input${fieldClass("age")}`} name="age" type="number" min="1" max="120" value={values.age} onChange={changeValue} placeholder="Age" />{errorText("age")}</div>
              <div><label className="h-label">City / Area</label><input className={`h-input${fieldClass("city_area")}`} name="city_area" value={values.city_area} onChange={changeValue} placeholder="City or area" />{errorText("city_area")}</div>
              <div><label className="h-label">Disposition List</label><select className={`h-select${fieldClass("disposition")}`} name="disposition" value={values.disposition} onChange={changeValue}><option value="">Select Disposition</option>{dispositions.map((item) => <option key={item} value={item}>{item}</option>)}</select>{errorText("disposition")}</div>
              {values.disposition === "Other" ? <div><label className="h-label">Other Disposition</label><input className={`h-input${fieldClass("disposition_other")}`} name="disposition_other" value={values.disposition_other} onChange={changeValue} placeholder="Enter disposition" />{errorText("disposition_other")}</div> : null}
              <div><label className="h-label">Appointment Date</label><input className={`h-input${fieldClass("appointment_date")}`} type="date" name="appointment_date" value={values.appointment_date} onChange={changeValue} />{errorText("appointment_date")}</div>
              <div><label className="h-label">Appointment Time</label><input className={`h-input${fieldClass("appointment_time")}`} type="time" name="appointment_time" value={values.appointment_time} onChange={changeValue} />{errorText("appointment_time")}</div>
              <div><label className="h-label">Consultation Fee</label><input className="h-input" name="consultation_fee" type="number" min="0" step="0.01" value={values.consultation_fee} onChange={changeValue} placeholder="0.00" /></div>
              <div><label className="h-label">Advance Fee</label><input className="h-input" name="advance_fee" type="number" min="0" step="0.01" value={values.advance_fee} onChange={changeValue} placeholder="0.00" /></div>
              <div><label className="h-label">Advance Transaction ID</label><input className={`h-input${fieldClass("advance_transaction_id")}`} name="advance_transaction_id" value={values.advance_transaction_id} onChange={changeValue} placeholder="Transaction ID" />{errorText("advance_transaction_id")}</div>
              <div><label className="h-label">Balance Fee</label><input className="h-input" value={balanceFee} readOnly /></div>
              <div><label className="h-label">Appointment Done</label><select className="h-select" name="appointment_done" value={values.appointment_done} onChange={changeValue}><option>NO</option><option>YES</option></select></div>
              <div><label className="h-label">Agent Name / Emp Code</label><input className="h-input" value={directWalkin} readOnly /></div>
              <div><label className="h-label">Source Name</label><input className="h-input" value={directWalkin} readOnly /></div>
            </div>
            <div className="h-note">Agent and source will be saved as Direct Hospital Walking for hospital walk-in patients.</div>
            <div className="h-actions"><button className="h-btn h-btn-primary" type="submit" disabled={saving}><FiSave size={13} /> {saving ? "Saving..." : "Save Entry"}</button><button className="h-btn h-btn-soft" type="button" onClick={resetForm}><FiRefreshCcw size={13} /> Reset</button></div>
          </form>
        </div>
        <footer className="h-footer"><strong>&copy; 2026 Sat Kartar Life Limited.</strong>&nbsp; All Rights Reserved</footer>
      </main>
    </div>
  );
};

export default HospitalPatientEntry;






